import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useNavigate} from 'react-router-dom'
import {useAppSelector} from '~/src/store/store-hooks'
import {useSignoutMutation, useRefreshSessionMutation} from '~/src/store/apis/auth-api'
import {extractUserFromToken} from '~/src/utils/jwt-utils'

// =============================================================================
// SESSION CONFIGURATION
// =============================================================================
export const SESSION_DURATION_MS = 30 * 60 * 1000 // 30 minutes
export const WARNING_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes before expiry
const CHECK_INTERVAL_MS = 30 * 1000 // Every 30 seconds
const ACTIVITY_THROTTLE_MS = 30 * 1000 // Throttle activity updates to once per 30s

const DEBUG_SESSION = false

// LocalStorage/SessionStorage keys
export const SESSION_EXPIRED_KEY = 'session_expired_reason'
export const SESSION_LOGIN_TIME_KEY = 'session_login_time'
export const SESSION_LAST_ACTIVITY_KEY = 'session_last_activity'

const debugLog = (message: string, data?: unknown) => {
  if (DEBUG_SESSION) {
    const timestamp = new Date().toLocaleTimeString()
    const prefix = `[SessionTimeout ${timestamp}]`
    if (data !== undefined) {
      console.warn(prefix, message, data)
    } else {
      console.warn(prefix, message)
    }
  }
}

export function setSessionLoginTime(): void {
  const now = Date.now().toString()
  localStorage.setItem(SESSION_LOGIN_TIME_KEY, now)
  localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now)
  debugLog('Session login time and last activity set')
}

function getSessionExpiration(): number | null {
  const lastActivityStr = localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)
  if (!lastActivityStr) {
    debugLog('getSessionExpiration: No last activity found')
    return null
  }

  const lastActivity = parseInt(lastActivityStr, 10)
  if (isNaN(lastActivity)) {
    debugLog('getSessionExpiration: Invalid last activity', lastActivityStr)
    return null
  }

  return lastActivity + SESSION_DURATION_MS
}

export function clearSessionLoginTime(): void {
  localStorage.removeItem(SESSION_LOGIN_TIME_KEY)
  localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY)
  debugLog('Session login time and last activity cleared')
}

interface SessionTimeoutContextType {
  showWarning: boolean
  minutesRemaining: number
  staySignedIn: () => void
  logout: () => void
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined)

interface SessionTimeoutProviderProps {
  children: React.ReactNode
}

export function SessionTimeoutProvider({children}: SessionTimeoutProviderProps) {
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.root.token)
  const refreshToken = useAppSelector((state) => state.root.refreshToken)
  const onSignIn = useAppSelector((state) => state.root.onSignIn)
  const [signout] = useSignoutMutation()
  const [refreshSessionMutation] = useRefreshSessionMutation()

  const [showWarning, setShowWarning] = useState(false)
  const [minutesRemaining, setMinutesRemaining] = useState(0)
  const [warningShown, setWarningShown] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastThrottleRef = useRef<number>(0)

  const updateLastActivity = useCallback(() => {
    const now = Date.now()
    if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return
    lastThrottleRef.current = now
    localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, now.toString())
    debugLog('Last activity updated')
  }, [])

  const performLogout = useCallback(
    async (reason: 'expired' | 'manual') => {
      debugLog(`performLogout called with reason: ${reason}`)

      if (reason === 'expired') {
        sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true')
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      clearSessionLoginTime()

      try {
        const email = localStorage.getItem('email')
        if (email) {
          await signout({email: JSON.parse(email)}).unwrap()
        } else {
          window.localStorage.clear()
          window.sessionStorage.clear()
        }
      } catch {
        window.localStorage.clear()
        window.sessionStorage.clear()
      }

      setShowWarning(false)
      setWarningShown(false)

      navigate('/signin', {replace: true})
    },
    [signout, navigate],
  )

  const checkSession = useCallback(() => {
    const expMs = getSessionExpiration()

    if (!expMs) {
      setShowWarning(false)
      return
    }

    const now = Date.now()
    const timeUntilExpMs = expMs - now
    const minutes = Math.ceil(timeUntilExpMs / 60000)

    debugLog(`checkSession: ${minutes} min remaining`, {
      expiresAt: new Date(expMs).toLocaleTimeString(),
      warningShown,
    })

    if (timeUntilExpMs <= 0) {
      debugLog('SESSION EXPIRED! Logging out...')
      void performLogout('expired')
      return
    }

    setMinutesRemaining(minutes)

    if (timeUntilExpMs <= WARNING_THRESHOLD_MS && !warningShown) {
      debugLog('SHOWING WARNING: Session expiring soon!')
      setShowWarning(true)
      setWarningShown(true)
    }
  }, [warningShown, performLogout])

  const staySignedIn = useCallback(() => {
    debugLog('staySignedIn called — resetting idle timer')

    updateLastActivity()
    lastThrottleRef.current = 0

    setShowWarning(false)
    setWarningShown(false)

    // Determine if current token is from IOP or Cognito
    const userInfo = token ? extractUserFromToken(token) : null
    const isIopSession = userInfo?.isIop ?? false

    if (isIopSession) {
      // IOP tokens: idle timer reset is sufficient; IOP token refresh
      // will be handled by a dedicated endpoint in a future phase.
      debugLog('IOP session — idle timer reset (token refresh deferred to future phase)')
    } else if (refreshToken) {
      refreshSessionMutation({refreshToken}).catch(() => {
        console.warn(
          '[SessionTimeout] Token refresh failed — idle timer was still reset. Next API call may trigger re-auth if token expired.',
        )
      })
    } else {
      debugLog('No refresh token available — session extended without token refresh')
    }
  }, [updateLastActivity, refreshToken, refreshSessionMutation, token])

  const logout = useCallback(() => {
    void performLogout('manual')
  }, [performLogout])

  // Activity listeners for idle detection
  useEffect(() => {
    if (!onSignIn || !token) return

    const events: Array<keyof DocumentEventMap> = [
      'mousemove',
      'mousedown',
      'click',
      'keydown',
      'scroll',
      'touchstart',
    ]

    const handleActivity = () => updateLastActivity()

    events.forEach((event) => document.addEventListener(event, handleActivity, {passive: true}))

    return () => {
      events.forEach((event) => document.removeEventListener(event, handleActivity))
    }
  }, [onSignIn, token, updateLastActivity])

  // Visibility change handler — catch up after tab was hidden
  useEffect(() => {
    if (!onSignIn || !token) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [onSignIn, token, checkSession])

  // Initialize last activity on sign-in
  useEffect(() => {
    if (onSignIn && token) {
      if (!localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) {
        setSessionLoginTime()
      }
    }
  }, [onSignIn, token])

  // Polling interval for session check
  useEffect(() => {
    if (!onSignIn || !token) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setShowWarning(false)
      setWarningShown(false)
      return
    }

    checkSession()
    intervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [onSignIn, token, checkSession])

  const contextValue: SessionTimeoutContextType = useMemo(
    () => ({
      showWarning,
      minutesRemaining,
      staySignedIn,
      logout,
    }),
    [showWarning, minutesRemaining, staySignedIn, logout],
  )

  return (
    <SessionTimeoutContext.Provider value={contextValue}>{children}</SessionTimeoutContext.Provider>
  )
}

export function useSessionTimeout(): SessionTimeoutContextType {
  const context = useContext(SessionTimeoutContext)

  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider')
  }

  return context
}
